import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, X, Edit2, Trash2, Save, Image as ImageIcon, ExternalLink, Github, LogOut, GitBranch, Upload, Link2 } from 'lucide-react';

interface Project {
  id: number;
  title: string;
  description: string;
  image: string;
  technologies: string[];
  githubUrl: string;
  liveUrl: string;
}

const AdminPanel: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isAddingProject, setIsAddingProject] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Omit<Project, 'id'>>({
    title: '',
    description: '',
    image: '',
    technologies: [],
    githubUrl: '',
    liveUrl: ''
  });
  const [techInput, setTechInput] = useState('');
  const [isCommitting, setIsCommitting] = useState(false);
  const [commitMessage, setCommitMessage] = useState('');
  const [imageUploadMethod, setImageUploadMethod] = useState<'url' | 'upload'>('url');
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    // Check authentication
    const isAuthenticated = sessionStorage.getItem('adminAuthenticated');
    if (!isAuthenticated) {
      window.location.href = '/admin/auth';
      return;
    }

    // Fetch projects from data source
    const fetchProjects = async () => {
      try {
        console.log('AdminPanel: Fetching projects from /data/projects.json...');
        // Try to fetch from the projects data file first
        const response = await fetch('/data/projects.json');
        console.log('AdminPanel: Response status:', response.status);
        if (response.ok) {
          const projectsData = await response.json();
          console.log('AdminPanel: Projects data loaded:', projectsData);
          setProjects(projectsData);
          localStorage.setItem('projects', JSON.stringify(projectsData));
        } else {
          console.log('AdminPanel: Failed to fetch from JSON, checking localStorage...');
          // If fetch fails, check localStorage as backup
          const savedProjects = localStorage.getItem('projects');
          if (savedProjects) {
            setProjects(JSON.parse(savedProjects));
          } else {
            // If no data anywhere, start with empty array
            console.log('AdminPanel: No projects data found, starting with empty array');
            setProjects([]);
          }
        }
      } catch (error) {
        console.error('AdminPanel: Failed to fetch projects:', error);
        // Fallback to localStorage if fetch fails
        const savedProjects = localStorage.getItem('projects');
        if (savedProjects) {
          setProjects(JSON.parse(savedProjects));
        } else {
          setProjects([]);
        }
      }
    };

    fetchProjects();

    // Cleanup function to clear localStorage when leaving admin panel
    const handleBeforeUnload = () => {
      localStorage.removeItem('projects');
    };

    // Add event listener for page unload
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      localStorage.removeItem('projects');
    };
  }, []);

  const saveProjects = (updatedProjects: Project[]) => {
    setProjects(updatedProjects);
    localStorage.setItem('projects', JSON.stringify(updatedProjects));
  };

  const commitToGit = async () => {
    if (!commitMessage.trim()) {
      alert('Please enter a commit message');
      return;
    }

    setIsCommitting(true);
    try {
      const response = await fetch('/api/commit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projects: projects,
          commitMessage: commitMessage
        })
      });

      const result = await response.json();

      if (result.success) {
        alert(`Changes committed successfully!\n\nMessage: ${commitMessage}\n\nProjects updated: ${result.projectsCount}`);
        setCommitMessage('');
      } else {
        alert(`Failed to commit: ${result.error}`);
      }
    } catch (error) {
      console.error('Commit error:', error);
      alert('Failed to commit changes. Please try again.');
    } finally {
      setIsCommitting(false);
    }
  };

  const handleLogout = () => {
    // Clear all admin-related data
    sessionStorage.removeItem('adminAuthenticated');
    localStorage.removeItem('projects');
    window.location.href = '/admin/auth';
  };

  const generateRandomFileName = (originalName: string) => {
    const extension = originalName.split('.').pop();
    const randomString = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    return `${randomString}.${extension}`;
  };

  const handleImageUpload = async (file: File) => {
    console.log('Starting image upload for file:', file.name, 'size:', file.size, 'type:', file.type);
    setIsUploading(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const base64Data = event.target?.result as string;
          console.log('File converted to base64, length:', base64Data.length);
          
          const fileName = generateRandomFileName(file.name);
          console.log('Generated filename:', fileName);
          
          const response = await fetch('/api/upload', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              imageData: base64Data,
              fileName: fileName,
              mimeType: file.type
            })
          });

          console.log('Upload response status:', response.status);
          
          const result = await response.json();
          console.log('Upload response:', result);

          if (result.success) {
            console.log('Upload successful, setting image URL:', result.imageUrl);
            setFormData({ ...formData, image: result.imageUrl });
          } else {
            console.error('Upload failed:', result);
            alert('Failed to upload image: ' + result.error + (result.details ? ' - ' + result.details : ''));
          }
        } catch (uploadError) {
          console.error('Upload processing error:', uploadError);
          alert('Failed to process upload: ' + uploadError.message);
        } finally {
          setIsUploading(false);
        }
      };

      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        alert('Failed to read file: ' + error);
        setIsUploading(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image. Please try again.');
      setIsUploading(false);
    }
  };

  const handleAddProject = () => {
    if (formData.title && formData.description && formData.image) {
      const newProject: Project = {
        id: Date.now(),
        ...formData
      };
      saveProjects([...projects, newProject]);
      resetForm();
      setIsAddingProject(false);
    }
  };

  const handleUpdateProject = (id: number) => {
    const updatedProjects = projects.map(project => 
      project.id === id ? { ...formData, id } : project
    );
    saveProjects(updatedProjects);
    resetForm();
    setEditingId(null);
  };

  const handleDeleteProject = (id: number) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      saveProjects(projects.filter(project => project.id !== id));
    }
  };

  const startEdit = (project: Project) => {
    setFormData({
      title: project.title,
      description: project.description,
      image: project.image,
      technologies: project.technologies,
      githubUrl: project.githubUrl,
      liveUrl: project.liveUrl
    });
    setEditingId(project.id);
    setIsAddingProject(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      image: '',
      technologies: [],
      githubUrl: '',
      liveUrl: ''
    });
    setTechInput('');
  };

  const addTechnology = () => {
    if (techInput.trim() && !formData.technologies.includes(techInput.trim())) {
      setFormData({
        ...formData,
        technologies: [...formData.technologies, techInput.trim()]
      });
      setTechInput('');
    }
  };

  const removeTechnology = (tech: string) => {
    setFormData({
      ...formData,
      technologies: formData.technologies.filter(t => t !== tech)
    });
  };

  return (
    <div className="min-h-screen bg-[#000000] p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Admin Panel</h1>
              <p className="text-zinc-400 text-sm sm:text-base">Manage your projects portfolio</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 rounded-lg bg-red-600/20 border border-red-500/30 text-red-400 font-medium hover:bg-red-600/30 transition-colors flex items-center gap-2 self-start sm:self-auto"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <motion.button
              onClick={() => {
                resetForm();
                setIsAddingProject(true);
                setEditingId(null);
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-6 py-3 rounded-lg bg-violet-600 text-white font-medium flex items-center justify-center gap-2 hover:bg-violet-700 transition-colors"
            >
              <Plus size={20} />
              Add New Project
            </motion.button>
          </div>
        </motion.div>

        {/* Git Commit Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 p-4 sm:p-6 rounded-2xl bg-[#0A0A0A] border border-white/10"
        >
          <h2 className="text-lg sm:text-xl font-bold text-white mb-4 flex items-center gap-2">
            <GitBranch size={20} className="text-violet-400" />
            Commit Changes to GitHub
          </h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={commitMessage}
              onChange={(e) => setCommitMessage(e.target.value)}
              placeholder="Enter commit message..."
              className="flex-1 px-4 py-2 sm:py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:border-violet-500/50 focus:outline-none transition-colors text-sm sm:text-base"
            />
            <motion.button
              onClick={commitToGit}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={isCommitting}
              className="px-4 sm:px-6 py-2 sm:py-3 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              {isCommitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span className="hidden sm:inline">Committing...</span>
                  <span className="sm:hidden">...</span>
                </>
              ) : (
                <>
                  <GitBranch size={18} />
                  <span className="hidden sm:inline">Commit & Push</span>
                  <span className="sm:hidden">Commit</span>
                </>
              )}
            </motion.button>
          </div>
          <p className="text-zinc-500 text-xs sm:text-sm mt-3">
            💡 Changes are saved locally. Use this to commit and push updates to GitHub repository.
          </p>
        </motion.div>

        {/* Add/Edit Project Form */}
        {isAddingProject && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 rounded-2xl bg-[#0A0A0A] border border-white/10"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">
                {editingId ? 'Edit Project' : 'Add New Project'}
              </h2>
              <button
                onClick={() => {
                  setIsAddingProject(false);
                  setEditingId(null);
                  resetForm();
                }}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="grid md:grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:border-violet-500/50 focus:outline-none transition-colors"
                  placeholder="Project title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Image</label>
                
                {/* Upload Method Toggle */}
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setImageUploadMethod('url')}
                    className={`flex-1 px-3 py-2 rounded-lg border transition-colors flex items-center justify-center gap-2 ${
                      imageUploadMethod === 'url' 
                        ? 'bg-violet-600/20 border-violet-500/30 text-violet-300' 
                        : 'bg-white/5 border-white/10 text-zinc-400 hover:border-white/20'
                    }`}
                  >
                    <Link2 size={16} />
                    URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageUploadMethod('upload')}
                    className={`flex-1 px-3 py-2 rounded-lg border transition-colors flex items-center justify-center gap-2 ${
                      imageUploadMethod === 'upload' 
                        ? 'bg-violet-600/20 border-violet-500/30 text-violet-300' 
                        : 'bg-white/5 border-white/10 text-zinc-400 hover:border-white/20'
                    }`}
                  >
                    <Upload size={16} />
                    Upload
                  </button>
                </div>

                {/* URL Input */}
                {imageUploadMethod === 'url' ? (
                  <input
                    type="url"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:border-violet-500/50 focus:outline-none transition-colors"
                    placeholder="https://example.com/image.jpg"
                  />
                ) : (
                  <div className="space-y-3">
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload(file);
                        }}
                        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-violet-600 file:text-white hover:file:bg-violet-700 cursor-pointer"
                        disabled={isUploading}
                      />
                      {isUploading && (
                        <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                          <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                    {formData.image && (
                      <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                        <p className="text-sm text-zinc-400 mb-2">Preview:</p>
                        <img 
                          src={formData.image} 
                          alt="Preview" 
                          className="w-full h-32 object-cover rounded-lg"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:border-violet-500/50 focus:outline-none transition-colors h-32 resize-none"
                  placeholder="Project description"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">GitHub URL</label>
                  <input
                    type="url"
                    value={formData.githubUrl}
                    onChange={(e) => setFormData({ ...formData, githubUrl: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:border-violet-500/50 focus:outline-none transition-colors"
                    placeholder="https://github.com/..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Live Demo URL</label>
                  <input
                    type="url"
                    value={formData.liveUrl}
                    onChange={(e) => setFormData({ ...formData, liveUrl: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:border-violet-500/50 focus:outline-none transition-colors"
                    placeholder="https://example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Technologies</label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={techInput}
                    onChange={(e) => setTechInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTechnology()}
                    className="flex-1 px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:border-violet-500/50 focus:outline-none transition-colors"
                    placeholder="Add technology (press Enter)"
                  />
                  <button
                    onClick={addTechnology}
                    className="px-6 py-3 rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-700 transition-colors"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.technologies.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 rounded-full bg-violet-600/20 border border-violet-500/30 text-violet-300 text-sm flex items-center gap-2"
                    >
                      {tech}
                      <button
                        onClick={() => removeTechnology(tech)}
                        className="hover:text-violet-100 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setIsAddingProject(false);
                  setEditingId(null);
                  resetForm();
                }}
                className="px-6 py-2 rounded-lg bg-white/10 text-white font-medium hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => editingId ? handleUpdateProject(editingId) : handleAddProject()}
                className="px-6 py-2 rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-700 transition-colors flex items-center gap-2"
              >
                <Save size={18} />
                {editingId ? 'Update' : 'Save'} Project
              </button>
            </div>
          </motion.div>
        )}

        {/* Projects List */}
        <div className="grid gap-4 sm:gap-6">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-4 sm:p-6 rounded-2xl bg-[#0A0A0A] border border-white/10"
            >
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-32 h-32 sm:h-24 rounded-lg overflow-hidden flex-shrink-0">
                  {project.image ? (
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-white/5 flex items-center justify-center">
                      <ImageIcon size={24} className="text-zinc-500" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-2 truncate">{project.title}</h3>
                  <p className="text-zinc-400 text-sm mb-3 line-clamp-2">{project.description}</p>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.technologies.map((tech) => (
                      <span
                        key={tech}
                        className="px-2 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-zinc-400"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-3 sm:gap-4">
                    <a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zinc-400 hover:text-white transition-colors flex items-center gap-1 text-sm"
                    >
                      <Github size={16} />
                      <span className="hidden sm:inline">GitHub</span>
                    </a>
                    <a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zinc-400 hover:text-white transition-colors flex items-center gap-1 text-sm"
                    >
                      <ExternalLink size={16} />
                      <span className="hidden sm:inline">Live Demo</span>
                    </a>
                  </div>
                </div>

                <div className="flex flex-row sm:flex-col gap-2">
                  <button
                    onClick={() => startEdit(project)}
                    className="p-2 rounded-lg bg-white/5 border border-white/10 text-zinc-400 hover:text-white hover:border-violet-500/30 transition-colors"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDeleteProject(project.id)}
                    className="p-2 rounded-lg bg-white/5 border border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-400/30 transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {projects.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <p className="text-zinc-500 text-lg">No projects yet. Click "Add New Project" to get started.</p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
